import React from "react";
import Box from "@mui/material/Box";
import CaseStudy from "./CaseStudy";
import CaseMasterScore from "./CaseMasterScore";
import Reattempt from "./Reattempt";
import FeedbackCard from "components/PerformanceReview4c/FeedbackCard";
import ReviewHeader from "components/PerformanceReview4c/ReviewHeader";

import { Session } from "utils";
import { useParams, useSearchParams } from "react-router-dom";
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts/SnackbarProvider';
import { getCaseMasterFeedbackByToken, getCaseMasterAttempts } from "services";
import { Break, WhiteBox } from "components/AppShell";
import { Categories, SubCategoryWeightages } from './constants';
import { Skeleton } from "@mui/material";
import { useNavigateWithClient } from "hooks";

const CaseMasterPerformance = () => {
  const { aid } = useParams();
  const [searchParams] = useSearchParams();

  const service = useServices();
  const snackbar = useSnackbar();
  const navigate = useNavigateWithClient();

  const [loading, setLoading] = React.useState(true);
  const [currentAttempt, setCurrentAttempt] = React.useState(null);
  const [caseMaster, setCaseMaster] = React.useState(null);
  const [followups, setFollowups] = React.useState([]);
  const [analysis, setAnalysis] = React.useState([]);
  const [details, setDetails] = React.useState(null);
  const [myScore, setMyScore] = React.useState(1);
  const [categories, setCategories] = React.useState(Categories);
  const [proctorRecords, setProctorRecords] = React.useState([]);
  const [entryPoint, setEntryPoint] = React.useState({});
  const [isGuestApp, setIsGuestApp] = React.useState(false);

  // Set currentAttempt from URL param
  React.useEffect(() => {
    if (aid) {
      setCurrentAttempt(aid);
    }
  }, [aid]);

  // Fetch attempt details when currentAttempt changes
  React.useEffect(() => {
    if (currentAttempt) {
      getAttempt(currentAttempt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttempt]);

  React.useEffect(() => {
    const at = searchParams.get('at');
    if (!at) return;

    setIsGuestApp(true);

    setLoading(true);
    
    // Mock data for guest app
    setTimeout(() => {
      const mockData = {
        caseMaster: [{
          _id: 'mock-case-master-1',
          title: 'SpiceJet Vs Indigo',
          tags: [{ name: 'Profitability' }],
          description: 'Your client is Spicejet which has recently seen a decline in profitability compared to Indigo. The management wants to understand the key factors driving this decline and develop a strategy to improve profitability.'
        }],
        finishedAt: new Date().toISOString(),
        startedAt: new Date(Date.now() - 35 * 60 * 1000).toISOString(), // 35 minutes ago
        analysis: [],
        followUps: [],
        feedback: {
          RELEVANCE: {
            score: 46,
            strength: "You demonstrated a clear understanding of the case scenario and effectively identified the key business problem. Your initial approach showed good analytical thinking.",
            weakness: "The case introduction could have been more structured with a clearer problem statement and initial hypothesis.",
            improvementTips: [
              "Start with a clear problem statement",
              "Provide a brief overview of the industry context",
              "State your initial hypothesis upfront"
            ]
          },
          DEPTH: {
            score: 76,
            strength: "You demonstrated a clear understanding of the case scenario and effectively identified the key business problem. Your initial approach showed good analytical thinking.",
            weakness: "The case introduction could have been more structured with a clearer problem statement and initial hypothesis.",
            improvementTips: [
              "Start with a clear problem statement",
              "Provide a brief overview of the industry context",
              "State your initial hypothesis upfront"
            ]
          },
          LOGIC: {
            score: 71,
            strength: "Your framework was logical and comprehensive, covering all major aspects of the profitability analysis. The structure was easy to follow.",
            weakness: "Some framework elements could have been prioritized better to focus on the most critical factors first.",
            improvementTips: [
              "Prioritize framework elements by impact",
              "Use MECE principle (Mutually Exclusive, Collectively Exhaustive)",
              "Consider both internal and external factors"
            ]
          },
          FEASIBILITY: {
            score: 92,
            strength: "Your framework was logical and comprehensive, covering all major aspects of the profitability analysis. The structure was easy to follow.",
            weakness: "Some framework elements could have been prioritized better to focus on the most critical factors first.",
            improvementTips: [
              "Prioritize framework elements by impact",
              "Use MECE principle (Mutually Exclusive, Collectively Exhaustive)",
              "Consider both internal and external factors"
            ]
          },
          PRECISION: {
            score: 86,
            strength: "You effectively analyzed the provided data and drew meaningful insights. Your calculations were accurate and supported your conclusions.",
            weakness: "The analysis could have been more thorough in exploring alternative explanations for the data patterns.",
            improvementTips: [
              "Look for multiple explanations for data patterns",
              "Validate findings with additional data points",
              "Consider seasonal and cyclical factors"
            ]
          },
          INSIGHTFULNESS: {
            score: 52,
            strength: "You effectively analyzed the provided data and drew meaningful insights. Your calculations were accurate and supported your conclusions.",
            weakness: "The analysis could have been more thorough in exploring alternative explanations for the data patterns.",
            improvementTips: [
              "Look for multiple explanations for data patterns",
              "Validate findings with additional data points",
              "Consider seasonal and cyclical factors"
            ]
          },
          THOROUGHNESS: {
            score: 67,
            strength: "You effectively analyzed the provided data and drew meaningful insights. Your calculations were accurate and supported your conclusions.",
            weakness: "The analysis could have been more thorough in exploring alternative explanations for the data patterns.",
            improvementTips: [
              "Look for multiple explanations for data patterns",
              "Validate findings with additional data points",
              "Consider seasonal and cyclical factors"
            ]
          },
          VALIDATION: {
            score: 57,
            strength: "You systematically tested your hypotheses and provided clear evidence for your conclusions. The testing approach was methodical.",
            weakness: "Some hypotheses could have been tested more rigorously with additional data validation.",
            improvementTips: [
              "Test hypotheses with multiple data sources",
              "Consider counterfactual analysis",
              "Validate assumptions with industry benchmarks"
            ]
          },
          MITIGATION: {
            score: 78,
            strength: "You systematically tested your hypotheses and provided clear evidence for your conclusions. The testing approach was methodical.",
            weakness: "Some hypotheses could have been tested more rigorously with additional data validation.",
            improvementTips: [
              "Test hypotheses with multiple data sources",
              "Consider counterfactual analysis",
              "Validate assumptions with industry benchmarks"
            ]
          },
          PRACTICALITY: {
            score: 69,
            strength: "You systematically tested your hypotheses and provided clear evidence for your conclusions. The testing approach was methodical.",
            weakness: "Some hypotheses could have been tested more rigorously with additional data validation.",
            improvementTips: [
              "Test hypotheses with multiple data sources",
              "Consider counterfactual analysis",
              "Validate assumptions with industry benchmarks"
            ]
          },
          ACTIONABILITY: {
            score: 74,
            strength: "Your conclusions were well-supported by the analysis and provided actionable recommendations. The recommendations were specific and implementable.",
            weakness: "The conclusion could have included more specific timelines and success metrics for the recommendations.",
            improvementTips: [
              "Include specific timelines for recommendations",
              "Define success metrics and KPIs",
              "Consider implementation challenges and mitigation strategies"
            ]
          },
          STRATEGIC_ALIGNMENT: {
            score: 95,
            strength: "Your conclusions were well-supported by the analysis and provided actionable recommendations. The recommendations were specific and implementable.",
            weakness: "The conclusion could have included more specific timelines and success metrics for the recommendations.",
            improvementTips: [
              "Include specific timelines for recommendations",
              "Define success metrics and KPIs",
              "Consider implementation challenges and mitigation strategies"
            ]
          },
          EXPLANATION_LOGICAL_COHERENCE: {
            score: 4,
            strength: "Your content was logically structured and demonstrated good understanding of business concepts. The flow of ideas was clear and coherent.",
            weakness: "Some explanations could have been more concise and focused on the key points.",
            improvementTips: [
              "Be more concise in explanations",
              "Focus on key insights and recommendations",
              "Use clear and simple language"
            ]
          },
          FLUENCY: {
            score: 4,
            strength: "Your speech was generally clear and understandable. You maintained good pace and volume throughout the presentation.",
            weakness: "There were some fluency issues with filler words and pauses that could be improved.",
            improvementTips: [
              "Practice reducing filler words",
              "Work on smoother transitions between ideas",
              "Practice speaking at a consistent pace"
            ]
          },
          VOCAL_DELIVERY: {
            score: 3,
            strength: "Your speech was generally clear and understandable. You maintained good pace and volume throughout the presentation.",
            weakness: "There were some fluency issues with filler words and pauses that could be improved.",
            improvementTips: [
              "Practice reducing filler words",
              "Work on smoother transitions between ideas",
              "Practice speaking at a consistent pace"
            ]
          }
        },
        proctorRecords: [],
      };

      setDetails({
        caseMaster: mockData.caseMaster[0],
        finishedAt: mockData.finishedAt,
        startedAt: mockData.startedAt
      });
      setCaseMaster(mockData.caseMaster[0]);
      setFollowups(mockData.followUps);
      setAnalysis(mockData.analysis);
      setProctorRecords(mockData.proctorRecords);
      calculateMyScore(mockData.feedback);
      curateDetailedFeedback(mockData.feedback);
      setEntryPoint({});
      setLoading(false);
    }, 1000);
  }, []);

  const transformSummaryToFeedback = React.useCallback((summary) => {
    const feedback = {};

    if (!summary || !summary.categories) {
      console.warn('No summary or categories found in API response');
      return feedback;
    }

    console.log('Summary from API:', summary);

    summary.categories.forEach(category => {
      console.log('Processing category:', category.categoryName, category);

      if (category.parameters && Array.isArray(category.parameters)) {
        category.parameters.forEach(param => {
          if (!param.parameterName) return;

          console.log('Processing parameter:', param.parameterName, 'improvementTips:', param.improvementTips);

          // Map parameter names to uppercase IDs (e.g., "Relevance" -> "RELEVANCE")
          const paramId = param.parameterName.toUpperCase().replace(/\s+/g, '_');

          // Handle improvementTips - can be array or string
          let improvementTips = [];
          if (Array.isArray(param.improvementTips)) {
            improvementTips = param.improvementTips;
          } else if (typeof param.improvementTips === 'string' && param.improvementTips) {
            improvementTips = [param.improvementTips];
          }

          feedback[paramId] = {
            score: param.score !== undefined ? param.score : 0,
            strength: param.strength || '',
            weakness: param.weakness || '',
            improvementTips: improvementTips
          };

          // If this is the Speech category, add features to subcategories
          if (category.categoryName === 'Speech' && category.features && Array.isArray(category.features)) {
            // Filter features for this parameter (FLUENCY or DELIVERY)
            const paramFeatures = category.features.filter(
              feature => feature.category === paramId
            );

            if (paramFeatures.length > 0) {
              feedback[paramId].features = paramFeatures;
            }
          }

          console.log('Created feedback for', paramId, ':', feedback[paramId]);
        });
      }
    });

    console.log('Final transformed feedback:', feedback);
    return feedback;
  }, []);

  // calculating overall score
  const calculateMyScore = React.useCallback((feedback) => {
    try {
      const total = SubCategoryWeightages.reduce((prev, curr) => {
        const { subCategory, weightage } = curr;
        const score = feedback[subCategory]?.score || 0;
        return prev + ((score * weightage) / 100);
      }, 0);

      //Calculates the average and rounding the result to two decimal places
      const avg = Math.round(total * 100) / 100;
      setMyScore(avg < 1 ? 1 : avg);
    } catch (error) {
      console.error('Error calculating score:', error);
      setMyScore(1);
    }
  }, []);

  const curateDetailedFeedback = React.useCallback((feedback) => {
    try {
      const newCategories = Categories.map((category) => {
        const { subCategories } = category;
        const newSubCategories = [];

        const total = subCategories.reduce((prev, curr) => {
          const { weightage, id } = curr;
          const feedbackData = feedback[id] || { score: 0, strength: '', weakness: '', improvementTips: [] };

          newSubCategories.push({ ...curr, ...feedbackData });
          return prev + ((feedbackData.score * weightage) / 100);
        }, 0);

        //Calculates the average and rounding the result to two decimal places
        const avg = Math.round(total * 100) / 100;
        let score = category.id === 'speech' ? avg : ((avg < 1) ? 1 : avg);

        return { ...category, score: score, subCategories: newSubCategories };
      });

      setCategories(newCategories);
    } catch (error) {
      console.error('Error curating feedback:', error);
    }
  }, []);

  const getAttempt = React.useCallback(async (attemptId) => {
    try {
      setLoading(true);

      const filters = {
        _id: attemptId,
        user: Session.userId,
        includeTotal: true,
        include: ["casemasterProblem"]
      };

      const response = await service.callService(getCaseMasterAttempts, filters);
      console.log('API Response:', response);

      const { total, casemasterAttempts } = response || {};

      if (!total || !casemasterAttempts || casemasterAttempts.length === 0) {
        throw new Error("Details not found!");
      }

      const attemptData = casemasterAttempts[0];
      console.log('Attempt Data (raw):', attemptData);

      const {
        casemasterProblem,
        casemasterContest,
        finishedAt,
        startedAt,
        audioAnalysis,
        userResponseAndPrompts,
        summary,
        proctorRecords,
      } = attemptData;

      const caseMasterData = Array.isArray(casemasterProblem) ? casemasterProblem[0] : casemasterProblem;

      console.log('Extracted Data:', {
        caseMasterData,
        summary,
        audioAnalysis,
        userResponseAndPrompts
      });

      // Transform new summary structure to old feedback format
      const feedback = transformSummaryToFeedback(summary);

      if (!caseMasterData) {
        throw new Error("Case master problem not found!");
      }

      setDetails({ caseMaster: caseMasterData, finishedAt, startedAt });
      setCaseMaster(caseMasterData);
      setFollowups(userResponseAndPrompts || []);
      setAnalysis(audioAnalysis || []);
      setProctorRecords(proctorRecords || []);

      // Use overallScore from API if available, otherwise calculate
      if (summary?.overallScore !== undefined) {
        setMyScore(summary.overallScore < 1 ? 1 : summary.overallScore);
      } else {
        calculateMyScore(feedback);
      }

      curateDetailedFeedback(feedback);
      setEntryPoint(casemasterContest ? { data: 'contest' } : {});
    } catch (error) {
      console.error('Error in getAttempt:', error);
      snackbar.error("Uh Oh! Unable to get details. Please try again.", false);
      navigate("/personalized-interview?type=case-master");
    } finally {
      setLoading(false);
    }
  }, [service, snackbar, navigate, transformSummaryToFeedback, calculateMyScore, curateDetailedFeedback]);

  // Handle attempt selection - navigate to new URL
  const handleAttemptChange = React.useCallback((attemptId) => {
    navigate(`/performance/case-master/${attemptId}`);
  }, [navigate]);

  return (
    <Box width='100%' display='flex' flexDirection='column' gap='16px'>

      <ReviewHeader
        currentAttempt={currentAttempt}
        setCurrentAttempt={handleAttemptChange}
        details={details}
        isGuestApp={isGuestApp}
        type="case-master"
      />

      {
        loading ?
          <Skeleton variant='rectangular' width={'100%'} height={300} /> :
          <>
            <WhiteBox>
              <CaseMasterScore 
                score={myScore} 
                proctorRecords={proctorRecords}
                maxScore={10}
                scoreLabel="My score:"
                tooltipTitle="Score based on case interview performance across multiple dimensions."
                showDescription={false}
              />
            </WhiteBox>

            <WhiteBox>
              <CaseStudy caseMaster={caseMaster} followUps={followups} />
            </WhiteBox>

            <WhiteBox>
              <FeedbackCard categories={categories} maxScore={100} isCaseStudy={true} />
            </WhiteBox>
          </>
      }

      {
        entryPoint?.data !== 'contest' && !isGuestApp && <Reattempt caseMaster={caseMaster} />
      }
    </Box>

  )
}

export default CaseMasterPerformance
